import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, gql, NetworkStatus } from '@apollo/client';
import { Paginador } from './HistorialPreguntas';
import './monitor_api.css';

// Pestaña "Monitor" de la pantalla API: cada pedido que recibió la API del ambiente, de quién fue, qué hizo y cómo
// terminó; los rechazos vienen explicados (por qué y cómo se resuelve). Se actualiza solo cada 5 minutos.
const GET_MONITOR = gql`
  query GetMonitorApi($entorno: String, $horas: Int, $pagina: Int, $porPagina: Int, $soloProblemas: Boolean) {
    getMonitorApi(entorno: $entorno, horas: $horas, pagina: $pagina, porPagina: $porPagina, soloProblemas: $soloProblemas) {
      base entorno horas actualizado registra truncado total pagina paginas porPagina error errorMensaje
      items {
        id fecha metodo ruta estado codigo resultado titulo causa solucion
        quienEmail quienNombre empresa integrador ip duracionMs requestId
      }
      resumen {
        total correctos rechazados errores ruido ultima primera
        cuentas { email nombre empresa pedidos correctos rechazados ultima }
        codigos { codigo estado cantidad titulo }
      }
    }
  }
`;

export const CADA_MS = 5 * 60 * 1000;
const POR_PAGINA = 50;
const TZ = 'America/Argentina/Buenos_Aires';
const VENTANAS = [[1, 'Última hora'], [6, '6 horas'], [24, '24 horas'], [72, '3 días'], [168, '7 días'], [720, '30 días']];
const RESULTADOS = {
  ok: ['tag--green', 'Correcto'],
  rechazado: ['tag--yellow', 'Rechazado'],
  error: ['tag--red', 'Error de la API'],
  ruido: ['tag--gray', 'Ruido'],
};
const fechaHora = (iso) => (iso ? new Date(iso).toLocaleString('es-AR', { timeZone: TZ, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '');
const hora = (iso) => (iso ? new Date(iso).toLocaleTimeString('es-AR', { timeZone: TZ, hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '');
const miles = (n) => new Intl.NumberFormat('es-AR').format(n || 0);
const duracion = (ms) => (ms === null || ms === undefined ? '' : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`);
const hace = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `hace ${s} s`;
  if (s < 3600) return `hace ${Math.floor(s / 60)} min`;
  if (s < 86400) return `hace ${Math.floor(s / 3600)} h`;
  return `hace ${Math.floor(s / 86400)} d`;
};

// Los textos de las explicaciones marcan campos y endpoints con acentos graves: se muestran como código.
export function ConCodigo({ texto }) {
  if (!texto) return null;
  const partes = String(texto).split(/(`[^`]+`)/g);
  return <>{partes.map((p, i) => (p.startsWith('`') && p.endsWith('`') ? <code key={i}>{p.slice(1, -1)}</code> : <React.Fragment key={i}>{p}</React.Fragment>))}</>;
}

function Kpi({ titulo, valor, pie, clase }) {
  return (
    <div className={`panel-card cia-kpi ${clase || ''}`}>
      <div className="cia-kpi-titulo">{titulo}</div>
      <div className="cia-kpi-valor">{miles(valor)}</div>
      {pie && <div className="cia-kpi-pie">{pie}</div>}
    </div>
  );
}

function Pedido({ it, abierto, alternar }) {
  const [clase, rotulo] = RESULTADOS[it.resultado] || RESULTADOS.rechazado;
  const quien = it.quienNombre || it.quienEmail
    ? <><b>{it.quienNombre || it.quienEmail}</b>{it.quienNombre && it.quienEmail ? <span className="ma-mail"> · {it.quienEmail}</span> : null}</>
    : <b>{it.integrador ? `integrador ${it.integrador}` : 'sin sesión'}</b>;
  return (
    <div className={`panel-card ma-item ma-item--${it.resultado} ${abierto ? 'ma-item--abierto' : ''}`}>
      <button type="button" className="ma-cabeza" onClick={alternar} aria-expanded={abierto}>
        <span className="ma-hora" title={it.fecha}>{hora(it.fecha)}</span>
        <span className={`tag ${clase}`}>{rotulo}</span>
        <span className="ma-titulo">{it.titulo}</span>
        <span className="ma-quien">{quien}</span>
        <span className="ma-tecnico">
          <span className={`hp-metodo hp-metodo--${it.metodo === 'POST' || it.metodo === 'PATCH' || it.metodo === 'DELETE' ? 'post' : 'otro'}`}>{it.metodo}</span>
          <code>{it.ruta}</code>
          <span className={`ma-estado ma-estado--${it.resultado}`}>{it.estado}{it.codigo ? ` · ${it.codigo}` : ''}</span>
        </span>
        <span className="ma-flecha" aria-hidden="true">{abierto ? '▾' : '▸'}</span>
      </button>
      {abierto && (
        <div className="ma-detalle">
          <div className="ma-bloque">
            <div className="ma-rotulo">{it.resultado === 'ok' ? 'Qué pasó' : 'Por qué'}</div>
            <p><ConCodigo texto={it.causa} /></p>
          </div>
          {it.solucion && (
            <div className="ma-bloque">
              <div className="ma-rotulo">Cómo se resuelve</div>
              <p><ConCodigo texto={it.solucion} /></p>
            </div>
          )}
          <div className="ma-datos">
            <span><b>Fecha</b> {fechaHora(it.fecha)}</span>
            {it.empresa && <span><b>Empresa</b> {it.empresa}</span>}
            {it.ip && <span><b>IP</b> {it.ip}</span>}
            {it.duracionMs !== null && it.duracionMs !== undefined && <span><b>Duración</b> {duracion(it.duracionMs)}</span>}
            {it.requestId && <span><b>X-Request-Id</b> <code>{it.requestId}</code></span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function MonitorApi({ entorno }) {
  const [horas, setHoras] = useState(24);
  const [soloProblemas, setSoloProblemas] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [abierto, setAbierto] = useState(null);
  const [, tic] = useState(0);
  const { data, loading, error, refetch, networkStatus } = useQuery(GET_MONITOR, {
    variables: { entorno, horas, pagina, porPagina: POR_PAGINA, soloProblemas },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    pollInterval: CADA_MS,
    notifyOnNetworkStatusChange: true,
  });
  // Cada 30 s se vuelve a dibujar para que "hace X" y la cuenta regresiva estén al día.
  useEffect(() => { const t = setInterval(() => tic((n) => n + 1), 30000); return () => clearInterval(t); }, []);
  const r = data?.getMonitorApi;
  const actualizando = loading || networkStatus === NetworkStatus.refetch || networkStatus === NetworkStatus.poll;
  const proxima = useMemo(() => (r ? new Date(new Date(r.actualizado).getTime() + CADA_MS) : null), [r]);
  const faltan = proxima ? Math.max(0, Math.round((proxima.getTime() - Date.now()) / 60000)) : null;
  const cambiar = (fn) => { fn(); setPagina(1); setAbierto(null); };

  return (
    <div className="ma">
      <div className="panel-card ma-barra">
        <div className="ma-barra-izq">
          <div className="ma-ventanas" role="tablist" aria-label="Ventana de tiempo">
            {VENTANAS.map(([h, t]) => (
              <button key={h} role="tab" aria-selected={horas === h} className={`ma-ventana ${horas === h ? 'ma-ventana--activa' : ''}`} onClick={() => cambiar(() => setHoras(h))}>{t}</button>
            ))}
          </div>
          <label className="ma-check">
            <input type="checkbox" checked={soloProblemas} onChange={(e) => cambiar(() => setSoloProblemas(e.target.checked))} /> Solo problemas
          </label>
        </div>
        <div className="ma-barra-der">
          <span className={`ma-latido ${actualizando ? 'ma-latido--activo' : ''}`} aria-hidden="true" />
          <span className="ma-actualizado">
            {actualizando ? 'Actualizando…' : r ? <>Actualizado {hora(r.actualizado)} · próxima en {faltan === 0 ? 'menos de 1 min' : `${faltan} min`}</> : 'Cargando…'}
          </span>
          <button className="btn-outline" onClick={() => refetch()} disabled={actualizando}>Actualizar ahora</button>
        </div>
      </div>

      {error && <div className="cs-aviso cs-aviso--error">{error.graphQLErrors?.[0]?.message || error.message}</div>}
      {r && r.error && <div className="cs-aviso cs-aviso--error"><b>No se pudo leer el monitor.</b> {r.errorMensaje}</div>}
      {r && !r.error && !r.registra && <div className="cs-aviso">{r.errorMensaje}</div>}
      {r && r.truncado && <div className="cs-aviso">Hay más pedidos de los que se muestran en esta ventana: se cargaron los últimos 5.000. Elegí una ventana más corta.</div>}

      {r && r.registra && (
        <>
          <div className="cia-kpis ma-kpis">
            <Kpi titulo={`Pedidos en ${VENTANAS.find(([h]) => h === horas)?.[1].toLowerCase() || `${horas} h`}`} valor={r.resumen.total} clase="cia-kpi--principal"
              pie={r.resumen.ultima ? `Último ${hace(r.resumen.ultima)} (${fechaHora(r.resumen.ultima)})` : 'Sin actividad en la ventana'} />
            <Kpi titulo="Correctos" valor={r.resumen.correctos} clase="ma-kpi--ok" pie={r.resumen.total ? `${Math.round((r.resumen.correctos * 100) / r.resumen.total)} % del total` : null} />
            <Kpi titulo="Rechazados" valor={r.resumen.rechazados} clase="ma-kpi--rechazado" pie="Respuestas 4xx a pedidos reales" />
            <Kpi titulo="Errores de la API" valor={r.resumen.errores} clase={r.resumen.errores ? 'ma-kpi--error' : ''} pie={r.resumen.ruido ? `Más ${miles(r.resumen.ruido)} de ruido (rutas inexistentes)` : 'Sin ruido de escáneres'} />
          </div>

          {(r.resumen.cuentas.length > 0 || r.resumen.codigos.length > 0) && (
            <div className="ma-resumenes">
              {r.resumen.cuentas.length > 0 && (
                <div className="panel-card ma-resumen">
                  <div className="ma-resumen-titulo">Quién usó la API</div>
                  {r.resumen.cuentas.map((c) => (
                    <div key={c.email || c.nombre} className="ma-cuenta">
                      <div className="ma-cuenta-quien"><b>{c.nombre || c.email}</b>{c.nombre && c.email ? <span className="ma-mail"> · {c.email}</span> : null}</div>
                      <div className="ma-cuenta-numeros">
                        <span>{miles(c.pedidos)} pedidos</span>
                        <span className="ma-num--ok">{miles(c.correctos)} ok</span>
                        {c.rechazados > 0 && <span className="ma-num--mal">{miles(c.rechazados)} rechazados</span>}
                        <span className="ma-cuenta-ultima">último {hace(c.ultima)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {r.resumen.codigos.length > 0 && (
                <div className="panel-card ma-resumen">
                  <div className="ma-resumen-titulo">Qué falló</div>
                  {r.resumen.codigos.map((c) => (
                    <div key={`${c.codigo}-${c.estado}`} className="ma-codigo">
                      <span className={`ma-estado ${c.estado >= 500 ? 'ma-estado--error' : c.codigo === 'not_found' && /inexistentes/.test(c.titulo || '') ? 'ma-estado--ruido' : 'ma-estado--rechazado'}`}>{c.estado} · {c.codigo}</span>
                      <span className="ma-codigo-titulo">{c.titulo}</span>
                      <b>{miles(c.cantidad)}</b>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="ma-lista-cabeza">
            <span className="hp-resumen"><b>{miles(r.total)}</b> {soloProblemas ? 'pedidos con problemas' : 'pedidos'} · página {r.pagina} de {r.paginas}</span>
            <span className="hp-nota-chica">Tocá un pedido para ver la explicación. Horario de Buenos Aires.</span>
          </div>
          {r.items.length === 0 ? (
            <div className="panel-card ma-item"><span className="hp-vacio">{soloProblemas ? 'Ningún pedido con problemas en esta ventana.' : 'La API no recibió pedidos en esta ventana.'}</span></div>
          ) : (
            <div className="ma-lista">
              {r.items.map((it) => <Pedido key={it.id} it={it} abierto={abierto === it.id} alternar={() => setAbierto(abierto === it.id ? null : it.id)} />)}
            </div>
          )}
          <Paginador pagina={r.pagina} paginas={r.paginas} ir={(p) => { setPagina(p); setAbierto(null); }} />
          <p className="cia-pie">Fuente: colección apirequests de la base {r.base} de MCP_NAWI (un registro por pedido, sin contenido de las conversaciones; se conserva 90 días). Los pedidos que no llegan a la API (bloqueados por Nginx o por el firewall) no aparecen acá.</p>
        </>
      )}
    </div>
  );
}
